import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, MenuItem, Paper, Stack, Tab, Tabs, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { Download, UploadFile } from '@mui/icons-material';
import api, { SoftwareRelease, softwareUpdatesService } from '../services/api';
import { useApp } from '../contexts/AppContext';
import { UPDATE_PRODUCTS } from '../utils/softwareProducts';

type LicenseOption = { id: string; product: string; reference: string; status: string; updatesStatus: string; company?: { name: string } };
const bytes = (size: number) => `${(size / 1024 / 1024).toLocaleString('it-IT', { maximumFractionDigits: 1 })} MB`;
async function errorMessage(error: any) {
  let body = error.response?.data;
  if (body instanceof Blob) { try { body = JSON.parse(await body.text()); } catch {} }
  return body?.error?.message || 'Operazione non riuscita. Riprova.';
}
export default function SoftwareUpdatesPage() {
  const { state } = useApp();
  const admin = state.auth.user?.role === 'administrator';
  const [product, setProduct] = useState('NextCut');
  const [licenses, setLicenses] = useState<LicenseOption[]>([]);
  const [licenseId, setLicenseId] = useState('');
  const [releases, setReleases] = useState<SoftwareRelease[]>([]);
  const [latestId, setLatestId] = useState<string | null>(null);
  const [limit, setLimit] = useState(300 * 1024 * 1024);
  const [version, setVersion] = useState('');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    if (admin) return;
    let current = true;
    api.get('/software-licenses').then(response => { if (current) setLicenses(response.data.data.licenses); })
      .catch(async e => { const message = await errorMessage(e); if (current) setError(message); });
    return () => { current = false; };
  }, [admin]);
  useEffect(() => {
    let current = true;
    setError(''); setReleases([]); setLatestId(null);
    if (!admin && !licenseId) { setLoading(false); return; }
    setLoading(true);
    softwareUpdatesService.getReleases(product, admin ? undefined : licenseId)
      .then(data => { if (current) { setReleases(data.releases); setLatestId(data.latestReleaseId); setLimit(data.maxUploadBytes); } })
      .catch(async e => { const message = await errorMessage(e); if (current) setError(message); })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [product, licenseId, admin, refresh]);
  const changeProduct = (value: string) => {
    setProduct(value); setLicenseId(''); setError(''); setSuccess(''); setFile(null); setVersion(''); setNotes(''); setFileInputKey(k => k + 1);
  };
  const upload = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!file || busy) return;
    setError(''); setSuccess('');
    if (file.size > limit) { setError(`Il file supera il limite di ${bytes(limit)}.`); return; }
    setBusy(true);
    try {
      await softwareUpdatesService.uploadRelease(product, version.trim(), notes.trim(), file);
      setSuccess(`Release ${product} ${version.trim()} pubblicata.`);
      setVersion(''); setNotes(''); setFile(null); setFileInputKey(k => k + 1); setRefresh(n => n + 1);
    } catch (e) { setError(await errorMessage(e)); }
    finally { setBusy(false); }
  };
  const manage = async (release: SoftwareRelease, action: 'toggle' | 'delete') => {
    if (!window.confirm(action === 'delete' ? `Eliminare definitivamente ${release.product} ${release.version}?` : `${release.isActive ? 'Ritirare' : 'Ripubblicare'} ${release.product} ${release.version}?`)) return;
    setBusy(true); setError(''); setSuccess('');
    try {
      if (action === 'delete') await softwareUpdatesService.deleteRelease(release.id);
      else await softwareUpdatesService.setActive(release.id, !release.isActive);
      setSuccess('Catalogo aggiornato.'); setRefresh(n => n + 1);
    } catch (e) { setError(await errorMessage(e)); }
    finally { setBusy(false); }
  };
  const download = async (release: SoftwareRelease) => {
    setBusy(true); setError(''); setSuccess('');
    try {
      const response = await softwareUpdatesService.downloadRelease(release.id, admin ? undefined : licenseId);
      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/octet-stream' }));
      const link = document.createElement('a');
      link.href = url; link.download = release.fileName;
      document.body.appendChild(link); link.click(); link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      setSuccess(`Download avviato: ${release.fileName}`);
    } catch (e) { setError(await errorMessage(e)); }
    finally { setBusy(false); }
  };
  const matchingLicenses = licenses.filter(l => l.product === product);
  return <Box>
    <Typography variant="h4" component="h1" sx={{ mb: 2 }}>Aggiornamenti software</Typography>
    <Typography color="text.secondary" sx={{ mb: 2 }}>
      Installer di NextCut, NextFrame e NextDrill. Per scaricare serve una licenza attiva con copertura aggiornamenti valida.
    </Typography>
    <Tabs value={product} onChange={(_, value) => changeProduct(value)} aria-label="Software da aggiornare" sx={{ mb: 3 }}>
      {UPDATE_PRODUCTS.map(p => <Tab key={p} label={p} value={p} disabled={busy} />)}
    </Tabs>
    {!admin && <Paper sx={{ p: 2, mb: 3 }}>
      {matchingLicenses.length ? <TextField select fullWidth label="Licenza per il download" value={licenseId} disabled={busy}
        onChange={e => { setLicenseId(e.target.value); setSuccess(''); }}>
        {matchingLicenses.map(l => <MenuItem key={l.id} value={l.id}>{l.reference}{l.company ? ` — ${l.company.name}` : ''}{l.status !== 'ACTIVE' ? ' (licenza non attiva)' : l.updatesStatus !== 'ACTIVE' ? ' (aggiornamenti non attivi)' : ''}</MenuItem>)}
      </TextField> : <Alert severity="info">Nessuna licenza {product} associata al tuo account.</Alert>}
    </Paper>}
    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
    {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
    {admin && <Paper component="form" onSubmit={upload} sx={{ p: 2, mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>Nuova release {product}</Typography>
      <Stack spacing={2}>
        <TextField label="Versione" placeholder="es. 1.4.0" value={version} onChange={e => setVersion(e.target.value)} required disabled={busy} inputProps={{ maxLength: 64 }} />
        <TextField label="Note di rilascio" value={notes} onChange={e => setNotes(e.target.value)} multiline minRows={2} disabled={busy} inputProps={{ maxLength: 4000 }} />
        <Box>
          <Button component="label" variant="outlined" startIcon={<UploadFile />} disabled={busy}>Seleziona installer
            <input key={fileInputKey} hidden type="file" aria-label="Installer software" accept=".exe,.msi,.zip" onChange={e => setFile(e.target.files?.[0] || null)} />
          </Button>
          <Typography variant="body2" sx={{ mt: 1 }}>{file ? `${file.name} (${bytes(file.size)})` : `Formati .exe, .msi, .zip. Massimo ${bytes(limit)}.`}</Typography>
        </Box>
        <Alert severity="info">La versione attiva più alta viene proposta ai programmi. Ritirando una release, la precedente versione attiva torna disponibile. Il download amministratore serve alla gestione dei pacchetti.</Alert>
        <Button type="submit" variant="contained" disabled={busy || !file || !version.trim()}>{busy ? 'Operazione in corso…' : 'Pubblica release'}</Button>
      </Stack>
    </Paper>}
    {loading ? <CircularProgress aria-label="Caricamento release" /> : <TableContainer component={Paper}>
      <Table aria-label={`Release ${product}`}>
        <TableHead><TableRow><TableCell>Versione</TableCell><TableCell>Installer</TableCell><TableCell>Note</TableCell><TableCell>Pubblicata il</TableCell><TableCell>Stato</TableCell><TableCell align="right">Azioni</TableCell></TableRow></TableHead>
        <TableBody>{releases.map(release => <TableRow key={release.id}>
          <TableCell>{release.version}</TableCell>
          <TableCell>{release.fileName}<Typography variant="caption" display="block">{bytes(release.fileSize)}</Typography><Typography variant="caption" display="block" sx={{ overflowWrap: 'anywhere' }}>SHA256: {release.sha256}</Typography></TableCell>
          <TableCell sx={{ whiteSpace: 'pre-wrap' }}>{release.notes || '—'}</TableCell>
          <TableCell>{new Date(release.createdAt).toLocaleString('it-IT')}</TableCell>
          <TableCell><Chip size="small" label={!release.isActive ? 'Ritirata' : release.id === latestId ? 'Ultima versione' : 'Storica'} color={release.isActive && release.id === latestId ? 'success' : 'default'} /></TableCell>
          <TableCell align="right"><Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
            <Button startIcon={<Download />} disabled={busy} onClick={() => download(release)}>Scarica</Button>
            {admin && <><Button disabled={busy} onClick={() => manage(release, 'toggle')}>{release.isActive ? 'Ritira' : 'Ripubblica'}</Button><Button color="error" disabled={busy} onClick={() => manage(release, 'delete')}>Elimina</Button></>}
          </Stack></TableCell>
        </TableRow>)}
        {!releases.length && <TableRow><TableCell colSpan={6} align="center">{!admin && !licenseId && matchingLicenses.length ? 'Seleziona una licenza per vedere gli aggiornamenti.' : 'Nessuna release disponibile.'}</TableCell></TableRow>}
        </TableBody>
      </Table>
    </TableContainer>}
  </Box>;
}
